import { useCallback, useEffect } from 'react';
import { ValuationsApproach } from 'common/types/valuation';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { RegistrationAPI } from '../useApproachDependencyGraph';
import { StableIdGenerator } from '../useApproachStableIds';

const useInitializeApproachDependencyGraph = ({
  approaches,
  dependencyGraph,
  stableIds,
  shouldBuild = true,
}: {
  approaches: ValuationsApproach[];
  dependencyGraph: RegistrationAPI;
  stableIds: StableIdGenerator;
  shouldBuild?: boolean;
}): void => {
  const { register, clear } = dependencyGraph;
  const { getOrCreateId } = stableIds;

  const resolvePublicCompsApproach = useCallback(
    (pcRef: string): ValuationsApproach | null => {
      if (!pcRef) {
        return null;
      }

      return (
        approaches.find(
          approach =>
            approach.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH &&
            (approach.panel_id === pcRef || approach.valuations_approach_gpc?.id?.toString() === pcRef)
        ) ?? null
      );
    },
    [approaches]
  );

  useEffect(() => {
    clear();

    if (!shouldBuild || approaches.length === 0) {
      return;
    }

    // first pass adds everything as node
    approaches.forEach(approach => {
      const panelId = approach.panel_id ?? approach.panelId;
      if (!panelId) {
        return;
      }

      register({
        approachId: getOrCreateId(panelId),
        approachType: approach.approach_type!,
        panelId,
      });
    });

    // second pass adds edges for backsolve approaches with market adjustment enabled
    const backsolves = approaches.filter(
      approach =>
        approach.approach_type === VALUATIONS_BACKSOLVE_APPROACH &&
        approach.valuations_approach_backsolve?.adjust_for_market &&
        approach.valuations_approach_backsolve?.gpc_approach
    );
    backsolves.forEach(approach => {
      const backsolvePanelId = approach.panel_id ?? approach.panelId;
      const { gpc_approach: publicCompsApproachRef } = approach.valuations_approach_backsolve ?? {};

      const pcApproach = resolvePublicCompsApproach(publicCompsApproachRef!.toString());
      const pcPanelId = pcApproach?.panelId;

      if (!pcPanelId) {
        return;
      }

      register({
        approachId: getOrCreateId(backsolvePanelId as string),
        approachType: approach.approach_type!,
        panelId: backsolvePanelId as string,
        sourceOfTruthType: VALUATIONS_PUBLIC_COMPANIES_APPROACH,
        sourceOfTruthStableId: getOrCreateId(pcPanelId),
      });
    });

    return () => {
      clear();
    };
  }, [approaches, clear, getOrCreateId, register, resolvePublicCompsApproach, shouldBuild]);
};

export default useInitializeApproachDependencyGraph;
